import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Scale,
  Handshake,
  Briefcase,
  FileSignature,
  ShieldCheck,
  ArrowRight,
  Mail,
  Phone,
  MessageCircle,
  CheckCircle2,
  Send,
} from "lucide-react";
import { site } from "@/lib/site";
import { submitContact } from "@/lib/api/intake.functions";

export const Route = createFileRoute("/attorneys")({
  head: () => ({
    meta: [
      { title: "Attorney Referral & Partnership | Nkadimeng Danny" },
      {
        name: "description",
        content:
          "Admitted attorneys can partner with Nkadimeng Danny for paralegal support, overflow work and client referrals across South Africa.",
      },
      { property: "og:title", content: "Attorney Referral & Partnership Network" },
      {
        property: "og:description",
        content:
          "Refer clients, collaborate on paralegal work, or join our admitted-attorney panel.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/attorneys" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/attorneys" }],
  }),
  component: AttorneysPage,
});

function AttorneysPage() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-24">
          <span className="gold-rule" />
          <p className="text-xs uppercase tracking-[0.25em] text-gold">
            For admitted practitioners
          </p>
          <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
            Attorney referral &amp; partnership network
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Matters requiring representation in court or before a tribunal are
            referred to admitted attorneys. Practitioners interested in
            collaboration, paralegal support or overflow work are warmly
            invited to get in touch.
          </p>
        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              icon: Handshake,
              title: "Refer your overflow",
              body: "We handle the paralegal heavy-lifting — drafting, bundling, evidence indexing and Court Online / CaseLines filing — so your firm can focus on advocacy.",
            },
            {
              icon: Briefcase,
              title: "Join our attorney panel",
              body: "Admitted attorneys who accept warm referrals from us — labour, family, civil, debt and consumer matters — are invited to apply to our panel.",
            },
            {
              icon: FileSignature,
              title: "White-label drafting",
              body: "Affidavits, contracts, charge sheets, demand letters, CCMA bundles — drafted to your house style and ready for your sign-off.",
            },
          ].map((b) => (
            <div
              key={b.title}
              className="rounded-2xl border border-border bg-card p-6 shadow-soft"
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-navy text-gold">
                <b.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg text-navy">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {b.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40 py-16">
        <div className="container-narrow">
          <span className="gold-rule" />
          <h2 className="font-display text-3xl text-navy md:text-4xl">
            How collaboration works
          </h2>
          <ol className="mt-8 grid gap-5 md:grid-cols-2">
            {[
              "You send us the brief — by email, WhatsApp or our intake form. We respond within one business day with scope and a flat fee.",
              "We draft, prepare or file to your specifications. Every document is delivered in editable format with a clean version for sign-off.",
              "Where the matter originated with us and requires representation, we hand it to you with a complete file: client brief, evidence, draft pleadings and timeline.",
              "Confidentiality and POPIA compliance are built in. We sign your firm's NDA or use ours.",
            ].map((step, i) => (
              <li
                key={step}
                className="flex gap-4 rounded-xl border border-border bg-card p-5 shadow-soft"
              >
                <span className="font-display text-3xl text-gold/70">
                  0{i + 1}
                </span>
                <p className="text-sm leading-relaxed text-foreground/85">
                  {step}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="grid gap-10 md:grid-cols-[1.2fr_1fr]">
          <div>
            <span className="gold-rule" />
            <h2 className="font-display text-3xl text-navy md:text-4xl">
              Why work with us
            </h2>
            <ul className="mt-6 space-y-3 text-sm text-foreground/85">
              {[
                "Founded and run by an LLB graduate of the University of South Africa.",
                "Strictly paralegal scope — we never hold ourselves out as attorneys or appear in court.",
                "POPIA-aligned client intake, document handling and storage.",
                "Transparent flat fees, no hidden disbursements.",
                "Comfortable in Sepedi, Zulu, Xhosa, Afrikaans and English.",
                "National coverage — remote-first, with in-person availability in Gauteng &amp; Limpopo.",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-gold/40 bg-card p-6 shadow-lifted md:p-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold">
              <Scale className="h-4 w-4" /> Get in touch
            </div>
            <h3 className="mt-2 font-display text-2xl text-navy">
              Open a partnership conversation
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Drop a line and we'll respond within one business day.
            </p>
            <div className="mt-5 space-y-3 text-sm">
              <a
                href={`mailto:${site.email}?subject=Attorney%20partnership%20enquiry`}
                className="flex items-center gap-3 rounded-md border border-input bg-background px-4 py-3 hover:border-navy"
              >
                <Mail className="h-4 w-4 text-navy" />
                {site.email}
              </a>
              <a
                href={`tel:${site.phoneIntl}`}
                className="flex items-center gap-3 rounded-md border border-input bg-background px-4 py-3 hover:border-navy"
              >
                <Phone className="h-4 w-4 text-navy" />
                {site.phone}
              </a>
              <a
                href={`https://wa.me/${site.whatsapp}?text=${encodeURIComponent("Hello Danny — I'm an admitted attorney interested in collaboration.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-md bg-[#25D366] px-4 py-3 text-white"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp the Director
              </a>
            </div>
            <Link
              to="/contact"
              className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-navy hover:text-gold"
            >
              Use the contact form instead <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <ReferMatterSection />

      <section className="container-narrow pb-24">
        <div className="rounded-2xl border-l-4 border-gold bg-secondary/60 p-6 md:p-8">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold">
            <ShieldCheck className="h-4 w-4" /> Scope reminder
          </div>
          <p className="mt-3 text-sm text-muted-foreground md:text-base">
            Nkadimeng Danny is an LLB graduate (UNISA) and is{" "}
            <strong className="text-foreground">not an admitted attorney</strong>{" "}
            of the High Court of South Africa. This practice provides
            paralegal, legal-support and compliance services only. We do not
            give legal advice, do not appear in court and do not operate a
            trust account.
          </p>
        </div>
      </section>
    </>
  );
}

function ReferMatterSection() {
  const send = useServerFn(submitContact);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState<null | { reference: string; email: string }>(null);

  return (
    <section className="border-t border-border bg-secondary/40 py-16" id="refer">
      <div className="container-narrow">
        <div className="mx-auto max-w-3xl">
          <span className="gold-rule" />
          <p className="text-xs uppercase tracking-[0.25em] text-gold">
            Refer a matter
          </p>
          <h2 className="mt-2 font-display text-3xl text-navy md:text-4xl">
            Send us a brief — we reply within one business day
          </h2>
          <p className="mt-3 text-muted-foreground">
            For admitted attorneys and law firms only. Share a short outline of the
            work and we'll come back with scope, turnaround and a flat fee.
            Confidentiality applies from the first email.
          </p>

          {done ? (
            <div className="mt-8 rounded-2xl border border-emerald-300 bg-emerald-50 p-6 text-emerald-900 shadow-soft">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                <div className="flex-1">
                  <p className="font-display text-lg">Brief received</p>
                  <p className="mt-1 text-sm">
                    Thank you. A confirmation email has been sent to{" "}
                    <strong>{done.email}</strong> with your reference number.
                    We'll respond within one business day.
                  </p>
                  <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm">
                    <span className="text-muted-foreground">Reference:</span>
                    <code className="font-mono font-semibold text-navy">
                      {done.reference}
                    </code>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                const matterType = String(f.get("matter_type") || "");
                const urgency = String(f.get("urgency") || "");
                const brief = String(f.get("brief") || "");
                const email = String(f.get("email") || "");
                setLoading(true);
                try {
                  const res = await send({
                    data: {
                      full_name: String(f.get("full_name") || ""),
                      email,
                      phone: String(f.get("phone") || ""),
                      service: "attorney-referral",
                      message:
                        `[ATTORNEY REFERRAL]\n` +
                        `Firm: ${String(f.get("firm") || "—")}\n` +
                        `Matter type: ${matterType || "—"}\n` +
                        `Urgency: ${urgency || "—"}\n\n` +
                        brief,
                    },
                  });
                  toast.success(`Brief sent — reference ${res.reference}`);
                  setDone({ reference: res.reference, email });
                } catch (err) {
                  toast.error(
                    err instanceof Error ? err.message : "Could not send brief",
                  );
                } finally {
                  setLoading(false);
                }
              }}
              className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-soft md:p-8"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field name="full_name" label="Your name" required autoComplete="name" />
                <Field name="firm" label="Firm" placeholder="e.g. Smith &amp; Partners Inc." />
                <Field name="email" type="email" label="Email" required autoComplete="email" />
                <Field name="phone" type="tel" label="Phone" autoComplete="tel" />
                <SelectField
                  name="matter_type"
                  label="Matter type"
                  required
                  options={[
                    { value: "", label: "Select…" },
                    { value: "labour-ccma", label: "Labour / CCMA" },
                    { value: "drafting", label: "Drafting (contracts / affidavits / pleadings)" },
                    { value: "court-online", label: "Court Online / CaseLines filing" },
                    { value: "popia", label: "POPIA / compliance" },
                    { value: "research", label: "Legal research / heads of argument" },
                    { value: "other", label: "Other" },
                  ]}
                />
                <SelectField
                  name="urgency"
                  label="Turnaround needed"
                  required
                  options={[
                    { value: "", label: "Select…" },
                    { value: "same-day", label: "Same day" },
                    { value: "48h", label: "Within 48 hours" },
                    { value: "1w", label: "Within a week" },
                    { value: "flex", label: "Flexible" },
                  ]}
                />
              </div>

              <div className="mt-4">
                <label className="text-sm font-medium text-navy">
                  Brief outline of the matter <span className="text-destructive">*</span>
                </label>
                <textarea
                  name="brief"
                  required
                  minLength={5}
                  maxLength={1800}
                  rows={6}
                  className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2.5 text-base md:text-sm focus:border-gold focus:outline-none"
                  placeholder="Keep it high level — no client-identifying details. We'll request specifics after acknowledging the brief."
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Do not include privileged or client-identifying information in this
                  form. We'll exchange a mutual NDA before any case-sensitive
                  material changes hands.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-navy px-6 py-3 text-base font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60 sm:w-auto md:text-sm"
              >
                <Send className="h-4 w-4" />
                {loading ? "Sending…" : "Send brief"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({
  name,
  label,
  type = "text",
  required,
  placeholder,
  autoComplete,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-navy">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <input
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2.5 text-base md:text-sm focus:border-gold focus:outline-none"
      />
    </div>
  );
}

function SelectField({
  name,
  label,
  options,
  required,
}: {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  required?: boolean;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-navy">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <select
        name={name}
        required={required}
        className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2.5 text-base md:text-sm focus:border-gold focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
